import { Component, OnInit, ChangeDetectionStrategy, inject, signal, input, linkedSignal, output } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { isEmpty } from 'lodash';
import { LoaderService } from '@app/services/loader.service';
import { CampaignService } from '@app/services/campaign.service';
import { ToastrService } from 'ngx-toastr';
import {smartDateFormat} from '@app/consts';
import {lastValueFrom} from 'rxjs';
import { InfoPopoverComponent } from '../../_components/info-popover/info-popover.component';

//
// @TODO: add this in a separate file to reuse
interface ScheduleDate {
  schedule_date?: string;
  schedule_time?: string;
  timezone?: any;
  timezoneText?: any;
  timezoneValue?: any;
}

@Component({
  selector: 'app-launch-confirmation-modal',
  templateUrl: './launch-confirmation-modal.component.html',
  styleUrls: ['./launch-confirmation-modal.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [InfoPopoverComponent]
})
export class LaunchConfirmationModalComponent implements OnInit {
  modal = inject(NgbActiveModal);
  private readonly loaderService = inject(LoaderService);
  private readonly campaignService = inject(CampaignService);
  private toastr = inject(ToastrService);

  public readonly scheduleInput = input<ScheduleDate>({}, {alias: 'schedule'});
  readonly schedule = linkedSignal(() => this.scheduleInput());
  /** The schedule was cleared in the dialog: the empty schedule the caller keeps from now on. */
  readonly scheduleCleared = output<ScheduleDate>();
  public readonly campaignID = input.required<string>();
  readonly code = signal<string | undefined>(undefined);
  readonly loading = signal(false);
  public readonly launchType = input('');
  readonly currentUser = input<any>({});
  readonly timeZoneValues = input<any[]>([]);

  async ngOnInit() {
    await this.getConfirmationCode();
  }

  close() {
    this.modal.close({action: 'cancel'});
  }

  closeWithAction(action: 'edit' | 'schedule-launch' | 'launch' | 'clear' | 'schedule' | 'launch-now') {
    //
    // Close modal with action
    // action = edit: should open edit modal where this was opened
    // action = launch: should launch the campaign where this was opened
    this.modal.close({
      action,
      code: this.code(),
    });
  }

  getScheduleDateTime(actual = false) {
    const schedule = this.schedule();
    return smartDateFormat(schedule?.schedule_date + ' ' + schedule?.schedule_time, schedule?.timezone, null, actual ? 'fullDateTimeWithTimeZone' : 'fullDateTime', actual ? schedule?.timezone : null);
  }

  hasSchedule() {
    return !isEmpty(this.schedule());
  }

  clearSchedule() {
    this.schedule.set({});
    this.scheduleCleared.emit(this.schedule());
  }

  async getConfirmationCode() {
    //
    // Set loading
    this.loaderService.show();
    this.loading.set(true);

    try {
      //
      // Get confirmation code
      const campaignLaunchCode: any = await lastValueFrom(this.campaignService.getLaunchCode(this.campaignID()));
      // Validate
      if (!campaignLaunchCode?.data?.launch_code) {
        //
        // Unset loading
        this.loaderService.hide(true);
        this.loading.set(false);

        //
        // Close modal
        this.modal.dismiss('cancel');

        return this.toastr.error(
          this.loaderService.getErrorMessage({ error: { msg: 'Could not get launch confirmation, try again' } }),
          'Error'
        );
      }

      //
      // Set launch code
      this.code.set(campaignLaunchCode.data.launch_code);

      //
      // Unset loading
      this.loaderService.hide(false);
      this.loading.set(false);
    } catch (error) {
      //
      // Unset loading
      this.loaderService.hide(true);
      this.loading.set(false);

      //
      // Close modal
      this.modal.dismiss('cancel');

      return this.toastr.error(this.loaderService.getErrorMessage(error), 'Error');
    }
  }
}
